import React from "react";

import {
  HighlightRange,
  IProductTemplateAttribute,
} from "components/dashboard/product-template-table/utils/interfaces";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import { Chips, ChipsChangeEvent } from "primereact/chips";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { CellSubType } from "utils/enums";
import { TimeSpan } from "utils/time-utils";
import { T } from "services/lang/LanguageService";
import OutputHighlight from "./components/OutputHighlight";

type MultiValueTypeProps = {
  attribute: IProductTemplateAttribute;
  onAttributeChange: React.Dispatch<React.SetStateAction<IProductTemplateAttribute>>;
};

const MultiValueType: React.FC<MultiValueTypeProps> = ({ attribute, onAttributeChange }) => {
  const bgHighlightList = [...(attribute.bgHighlightRanges ?? [])];

  const onFieldChange = (e: ChipsChangeEvent | DropdownChangeEvent | InputSwitchChangeEvent) => {
    let { value } = e.target;

    if (attribute.subType === CellSubType.TIMESPAN && e.target.name === "selectionValues") {
      value = value?.map((v: string) => parseTimeSpan(v));
    }

    onAttributeChange(prev => ({
      ...prev,
      [e.target.name]: value,
    }));
  };

  const handleChipTemplate = (item: string) => {
    const timeSpan = parseTimeSpan(item);
    return getTimeSpanStringTemplate(timeSpan);
  };

  const parseTimeSpan = (string: string): TimeSpan => {
    const times = string?.split(":");
    const days = parseInt(times[0], 10) || 0;
    const hours = parseInt(times[1], 10) || 0;
    const minutes = parseInt(times[2], 10) || 0;
    const seconds = parseInt(times[3], 10) || 0;
    return { days, hours, minutes, seconds };
  };

  const getTimeSpanStringTemplate = (timeSpan: TimeSpan) => {
    return `${timeSpan.days}d ${timeSpan.hours}h ${timeSpan.minutes}m ${timeSpan.seconds}s`;
  };

  const selections = attribute.selectionValues?.map(sv =>
    typeof sv !== "string" ? `${sv.days}:${sv.hours}:${sv.minutes}:${sv.seconds}` : sv
  );

  const selectionValuesChipProps = {
    value: selections ?? [],
    onChange: onFieldChange,
    name: "selectionValues",
    separator: ",",
    disabled: !attribute.subType,
  };

  const defaultValues: string[] = Array.isArray(attribute.defaultValue)
    ? attribute.defaultValue.map(String)
    : [String(attribute.defaultValue)];

  const defaultValuesChipProps = {
    value: defaultValues ?? [],
    onChange: onFieldChange,
    name: "defaultValue",
    separator: ",",
    disabled: !attribute.subType,
  };

  const updateAttributeChange = (newBgList: HighlightRange[]) => {
    onAttributeChange(prev => ({
      ...prev,
      bgHighlightRanges: newBgList,
    }));
  };

  return (
    <>
      <div className="p-field p-col-2">
        <label>{T("word.subtype")}</label>
        <Dropdown
          value={attribute.subType}
          options={Object.keys(CellSubType)}
          onChange={onFieldChange}
          name="subType"
        />
      </div>
      <div className="p-field p-col-4">
        <label>{T("label.default-values")}</label>
        <Chips {...defaultValuesChipProps} />
      </div>
      <div className="p-field p-col-4">
        <label>{T("word.selection-values")}</label>
        {attribute.subType === CellSubType.TIMESPAN ? (
          <Chips
            {...selectionValuesChipProps}
            placeholder="Timespans use 0:0:0:0 format (days:hours:minutes:seconds)"
            itemTemplate={handleChipTemplate}
          />
        ) : (
          <Chips {...selectionValuesChipProps} />
        )}
      </div>
      <div className="p-field p-col-2">
        <label htmlFor="inlineDisplay">{T("label.display-in-row")}</label>
        <div>
          <InputSwitch
            checked={attribute.inlineDisplay ?? false}
            onChange={onFieldChange}
            name="inlineDisplay"
          />
        </div>
      </div>
      {attribute.subType === CellSubType.DATASET && (
        <OutputHighlight bgHighlights={bgHighlightList} onAttributeChange={updateAttributeChange} />
      )}
    </>
  );
};

export default MultiValueType;
